import { useCallback, useEffect, useState } from "react";
import { APP_CURRENCY, APP_LOCALE } from "../../../config/locale";
import { Modal } from "../../../components/Modal";
import {
  ApiError,
  approveWorkVariation,
  createWorkVariation,
  deleteWorkVariation,
  getWorkVariationMap,
  getWorkVariations,
  reopenWorkVariation,
  rejectWorkVariation,
  updateWorkVariation,
  type WorkVariationDto,
  type WorkVariationKind,
  type WorkVariationMapDto,
  type WorkVariationStatus,
  type WorkVariationsVm,
} from "../../../workVariations/workVariationsApi";

interface WorkVariationsTabProps {
  workId: number;
}

const currencyFormatter = new Intl.NumberFormat(APP_LOCALE, {
  style: "currency",
  currency: APP_CURRENCY,
});

const percentFormatter = new Intl.NumberFormat(APP_LOCALE, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return currencyFormatter.format(0);
  }
  return currencyFormatter.format(value);
};

// Valor absoluto formatado (o sinal +/− é aplicado à parte pelo chamador para
// controlar cor e evitar ambiguidade do sinal do locale).
const formatCurrencyAbs = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return currencyFormatter.format(0);
  }
  return currencyFormatter.format(Math.abs(value));
};

const formatPercent = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "—";
  }
  return `${percentFormatter.format(value)}%`;
};

// Parse tolerante (aceita vírgula ou ponto), como nos restantes tabs da obra.
const parseNumber = (value: string): number => {
  const normalized = value.trim().replace(/\s/g, "").replace(",", ".");
  if (!normalized) return 0;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const kindLabel = (kind: WorkVariationKind): string =>
  kind === "addition" ? "Trabalho a mais" : "Trabalho a menos";

const kindBadgeClass = (kind: WorkVariationKind): string =>
  kind === "addition"
    ? "bg-green-500/10 text-green-700"
    : "bg-destructive/10 text-destructive";

const statusLabel = (status: WorkVariationStatus): string => {
  switch (status) {
    case "approved":
      return "Aprovada";
    case "rejected":
      return "Rejeitada";
    default:
      return "Rascunho";
  }
};

const statusBadgeClass = (status: WorkVariationStatus): string => {
  if (status === "approved") {
    return "bg-green-500/10 text-green-700 border border-border";
  }
  if (status === "rejected") {
    return "bg-destructive/10 text-destructive border border-border";
  }
  return "bg-muted text-secondary-text border border-border";
};

type LifecycleAction = "approve" | "reopen";

interface VariationForm {
  description: string;
  kind: WorkVariationKind;
  amount: string;
  notes: string;
}

interface RejectForm {
  rejectionReason: string;
}

type ModalState =
  | { kind: "create"; form: VariationForm }
  | { kind: "edit"; variation: WorkVariationDto; form: VariationForm }
  | { kind: "reject"; variation: WorkVariationDto; form: RejectForm }
  | null;

const inputClass =
  "px-3 py-2 border border-border rounded bg-background text-foreground text-sm";
const linkAction =
  "underline cursor-pointer transition-opacity hover:opacity-70 disabled:opacity-50 disabled:cursor-not-allowed";

export function WorkVariationsTab({ workId }: WorkVariationsTabProps) {
  const [vm, setVm] = useState<WorkVariationsVm | null>(null);
  const [map, setMap] = useState<WorkVariationMapDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [actioning, setActioning] = useState<{
    id: number;
    kind: LifecycleAction;
  } | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const [modal, setModal] = useState<ModalState>(null);
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setIsLoading(true);
      setError(null);
      try {
        const [vmData, mapData] = await Promise.all([
          getWorkVariations(workId),
          getWorkVariationMap(workId),
        ]);
        setVm(vmData);
        setMap(mapData);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Não foi possível carregar as variações.",
        );
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [workId],
  );

  useEffect(() => {
    load();
  }, [load]);

  const variations = vm?.variations ?? [];

  // ---- Abertura de modais ---------------------------------------------------
  const openCreate = () => {
    setError(null);
    setModal({
      kind: "create",
      form: { description: "", kind: "addition", amount: "", notes: "" },
    });
  };

  const openEdit = (variation: WorkVariationDto) => {
    setError(null);
    setModal({
      kind: "edit",
      variation,
      form: {
        description: variation.description ?? "",
        kind: variation.kind,
        amount: String(variation.amount ?? ""),
        notes: variation.notes ?? "",
      },
    });
  };

  const openReject = (variation: WorkVariationDto) => {
    setError(null);
    setModal({
      kind: "reject",
      variation,
      form: { rejectionReason: variation.rejectionReason ?? "" },
    });
  };

  const closeModal = () => setModal(null);

  // ---- Patches de formulário (por-tipo, com narrowing) ----------------------
  const patchVariation = (patch: Partial<VariationForm>) =>
    setModal((prev) =>
      prev && (prev.kind === "create" || prev.kind === "edit")
        ? { ...prev, form: { ...prev.form, ...patch } }
        : prev,
    );
  const patchReject = (patch: Partial<RejectForm>) =>
    setModal((prev) =>
      prev && prev.kind === "reject"
        ? { ...prev, form: { ...prev.form, ...patch } }
        : prev,
    );

  // ---- Submissões -----------------------------------------------------------
  const handleCreate = async () => {
    if (!modal || modal.kind !== "create" || isSaving) return;
    const { form } = modal;
    if (!form.description.trim()) {
      setError("Descreva a variação.");
      return;
    }
    if (parseNumber(form.amount) <= 0) {
      setError("Indique um valor positivo para a variação.");
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await createWorkVariation(workId, {
        description: form.description.trim(),
        kind: form.kind,
        amount: parseNumber(form.amount),
        notes: form.notes.trim() || null,
      });
      closeModal();
      await load(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Não foi possível criar a variação.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!modal || modal.kind !== "edit" || isSaving) return;
    const { variation, form } = modal;
    if (!form.description.trim()) {
      setError("Descreva a variação.");
      return;
    }
    if (parseNumber(form.amount) <= 0) {
      setError("Indique um valor positivo para a variação.");
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await updateWorkVariation(workId, variation.id, {
        description: form.description.trim(),
        kind: form.kind,
        amount: parseNumber(form.amount),
        notes: form.notes.trim() || null,
      });
      closeModal();
      await load(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Não foi possível guardar a variação.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleReject = async () => {
    if (!modal || modal.kind !== "reject" || isSaving) return;
    const { variation, form } = modal;
    setIsSaving(true);
    setError(null);
    try {
      await rejectWorkVariation(workId, variation.id, {
        rejectionReason: form.rejectionReason.trim() || null,
      });
      closeModal();
      await load(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Não foi possível rejeitar a variação.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const runLifecycle = async (variationId: number, kind: LifecycleAction) => {
    if (actioning !== null) return;
    setActioning({ id: variationId, kind });
    setError(null);
    try {
      if (kind === "approve") {
        await approveWorkVariation(workId, variationId);
      } else {
        await reopenWorkVariation(workId, variationId);
      }
      await load(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : kind === "approve"
            ? "Não foi possível aprovar a variação."
            : "Não foi possível reabrir a variação.",
      );
    } finally {
      setActioning(null);
    }
  };

  const handleDelete = async (variationId: number) => {
    if (deletingId !== null) return;
    setDeletingId(variationId);
    setError(null);
    try {
      await deleteWorkVariation(workId, variationId);
      setConfirmDeleteId(null);
      await load(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setConfirmDeleteId(null);
        await load(true);
      } else {
        setError(
          err instanceof Error
            ? err.message
            : "Não foi possível eliminar a variação.",
        );
      }
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div
          className="border border-border bg-destructive/10 text-destructive px-4 py-3"
          style={{ borderRadius: "var(--radius)" }}
        >
          {error}
        </div>
      )}

      {/* Mapa de variações — cascata orçamento base → revisto + margem */}
      <div
        className="bg-card border border-border px-4 py-4"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <h3
          className="text-foreground mb-3"
          style={{
            fontSize: "var(--text-section-title)",
            fontWeight: "var(--font-weight-semi-bold)",
          }}
        >
          Mapa de variações
        </h3>
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between gap-3">
            <span className="text-secondary-text">Orçamento base (venda)</span>
            <span className="text-foreground whitespace-nowrap">
              {formatCurrency(map?.baseSell)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-secondary-text">+ Trabalhos a mais</span>
            <span className="text-green-700 whitespace-nowrap">
              + {formatCurrencyAbs(map?.approvedAdditions)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-secondary-text">− Trabalhos a menos</span>
            <span className="text-destructive whitespace-nowrap">
              − {formatCurrencyAbs(map?.approvedReductions)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3 border-t border-border pt-3 mt-1">
            <span className="text-foreground font-medium">
              Orçamento revisto
            </span>
            <span
              className="text-foreground font-medium whitespace-nowrap"
              style={{ fontSize: "var(--text-section-title)" }}
            >
              {formatCurrency(map?.revisedSell)}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 border-t border-border pt-3 mt-3 text-sm">
          <span className="text-secondary-text">
            Custo realizado:{" "}
            <span className="text-foreground">
              {formatCurrency(map?.actualCost)}
            </span>
          </span>
          <span className="text-secondary-text">
            Margem base:{" "}
            <span className="text-foreground">
              {formatCurrency(map?.baseMargin)}
            </span>
          </span>
          <span className="text-secondary-text">
            Margem revista:{" "}
            <span
              className={
                (map?.revisedMargin ?? 0) >= 0
                  ? "text-green-700 font-medium"
                  : "text-destructive font-medium"
              }
            >
              {formatCurrency(map?.revisedMargin)} (
              {formatPercent(map?.revisedMarginPercent)})
            </span>
          </span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="text-secondary-text text-sm">
          {variations.length} variaç{variations.length === 1 ? "ão" : "ões"} ·
          Líquido aprovado {formatCurrency(vm?.netApproved ?? 0)}
        </span>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center justify-center px-4 py-2 min-h-[44px] bg-primary text-primary-foreground cursor-pointer text-sm"
          style={{ borderRadius: "var(--radius-button)" }}
        >
          + Nova variação
        </button>
      </div>

      {/* Tabela */}
      <div
        className="bg-card border border-border overflow-x-auto"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <table className="w-full" style={{ minWidth: 900 }}>
          <thead>
            <tr className="border-b border-border bg-muted text-left">
              <th className="px-4 py-3" style={{ width: 110 }}>
                Nº
              </th>
              <th className="px-4 py-3">Descrição</th>
              <th className="px-4 py-3" style={{ width: 160 }}>
                Tipo
              </th>
              <th className="px-4 py-3 text-right" style={{ width: 140 }}>
                Valor
              </th>
              <th className="px-4 py-3" style={{ width: 120 }}>
                Estado
              </th>
              <th className="px-4 py-3" style={{ width: 250 }}>
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-6 text-center text-secondary-text"
                >
                  A carregar…
                </td>
              </tr>
            ) : variations.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-6 text-center text-secondary-text"
                >
                  Sem variações registadas. Crie a primeira variação acima.
                </td>
              </tr>
            ) : (
              variations.map((variation) => {
                const isAddition = variation.kind === "addition";
                // Variações rejeitadas não contam no mapa: esbatemos a linha e
                // riscamos descrição+valor para deixá-lo óbvio (badge/motivo
                // mantêm-se legíveis). opacity-60/line-through não existem no
                // Tailwind pré-compilado → aplicados via style inline.
                const isRejected = variation.status === "rejected";
                return (
                  <tr
                    key={variation.id}
                    className="border-b border-border last:border-b-0"
                    style={isRejected ? { opacity: 0.6 } : undefined}
                  >
                    <td className="px-4 py-3 break-words">
                      {variation.number || "—"}
                    </td>
                    <td
                      className="px-4 py-3 break-words"
                      style={
                        isRejected
                          ? { textDecoration: "line-through" }
                          : undefined
                      }
                    >
                      {variation.description || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 text-xs ${kindBadgeClass(variation.kind)}`}
                        style={{ borderRadius: "var(--radius)" }}
                      >
                        {kindLabel(variation.kind)}
                      </span>
                    </td>
                    <td
                      className={`px-4 py-3 text-right whitespace-nowrap font-medium ${
                        isAddition ? "text-green-700" : "text-destructive"
                      }`}
                      style={
                        isRejected
                          ? { textDecoration: "line-through" }
                          : undefined
                      }
                    >
                      {isAddition ? "+ " : "− "}
                      {formatCurrencyAbs(variation.signedAmount)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 text-xs ${statusBadgeClass(variation.status)}`}
                        style={{ borderRadius: "var(--radius)" }}
                        title={
                          variation.status === "rejected" &&
                          variation.rejectionReason
                            ? variation.rejectionReason
                            : undefined
                        }
                      >
                        {statusLabel(variation.status)}
                      </span>
                      {variation.status === "rejected" &&
                        variation.rejectionReason && (
                          <div className="mt-1 text-xs text-secondary-text break-words">
                            {variation.rejectionReason}
                          </div>
                        )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-3 flex-wrap items-center">
                        {variation.status === "draft" && (
                          <button
                            type="button"
                            className={`${linkAction} text-primary`}
                            disabled={actioning?.id === variation.id}
                            onClick={() => runLifecycle(variation.id, "approve")}
                          >
                            {actioning?.id === variation.id &&
                            actioning.kind === "approve"
                              ? "A aprovar…"
                              : "Aprovar"}
                          </button>
                        )}
                        {variation.status === "draft" && (
                          <button
                            type="button"
                            className={`${linkAction} text-destructive`}
                            onClick={() => openReject(variation)}
                          >
                            Rejeitar
                          </button>
                        )}
                        {variation.status === "draft" && (
                          <button
                            type="button"
                            className={`${linkAction} text-primary`}
                            onClick={() => openEdit(variation)}
                          >
                            Editar
                          </button>
                        )}
                        {(variation.status === "approved" ||
                          variation.status === "rejected") && (
                          <button
                            type="button"
                            className={`${linkAction} text-primary`}
                            disabled={actioning?.id === variation.id}
                            onClick={() => runLifecycle(variation.id, "reopen")}
                          >
                            {actioning?.id === variation.id &&
                            actioning.kind === "reopen"
                              ? "A reabrir…"
                              : "Reabrir"}
                          </button>
                        )}
                        {(variation.status === "draft" ||
                          variation.status === "rejected") &&
                          (confirmDeleteId === variation.id ? (
                            <span className="inline-flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-secondary-text">
                                Apagar?
                              </span>
                              <button
                                type="button"
                                disabled={deletingId === variation.id}
                                onClick={() => handleDelete(variation.id)}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-destructive/10 text-destructive cursor-pointer disabled:opacity-50"
                                style={{ borderRadius: "var(--radius)" }}
                              >
                                {deletingId === variation.id
                                  ? "A eliminar…"
                                  : "Confirmar"}
                              </button>
                              <button
                                type="button"
                                disabled={deletingId === variation.id}
                                onClick={() => setConfirmDeleteId(null)}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-border text-foreground hover:bg-accent/50 cursor-pointer disabled:opacity-50"
                                style={{ borderRadius: "var(--radius)" }}
                              >
                                Cancelar
                              </button>
                            </span>
                          ) : (
                            <button
                              type="button"
                              className={`${linkAction} text-destructive`}
                              onClick={() => setConfirmDeleteId(variation.id)}
                            >
                              Eliminar
                            </button>
                          ))}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Modal — Nova variação / Editar variação */}
      {modal && (modal.kind === "create" || modal.kind === "edit") && (
        <Modal
          title={
            modal.kind === "create"
              ? "Nova variação"
              : `Editar variação ${modal.variation.number || ""}`.trim()
          }
          onClose={closeModal}
          maxWidthClass="max-w-2xl"
          footer={
            <>
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 border border-border"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={modal.kind === "create" ? handleCreate : handleUpdate}
                disabled={isSaving}
                className="px-4 py-2 bg-primary text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving
                  ? "A guardar…"
                  : modal.kind === "create"
                    ? "Criar variação"
                    : "Guardar"}
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Descrição *
              <textarea
                rows={2}
                value={modal.form.description}
                onChange={(event) =>
                  patchVariation({ description: event.target.value })
                }
                className={inputClass}
              />
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Tipo
                <select
                  value={modal.form.kind}
                  onChange={(event) =>
                    patchVariation({
                      kind: event.target.value as WorkVariationKind,
                    })
                  }
                  className={inputClass}
                >
                  <option value="addition">Trabalho a mais</option>
                  <option value="reduction">Trabalho a menos</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm text-secondary-text">
                Valor
                <input
                  type="text"
                  inputMode="decimal"
                  value={modal.form.amount}
                  onChange={(event) =>
                    patchVariation({ amount: event.target.value })
                  }
                  placeholder="0,00"
                  className={inputClass}
                />
              </label>
            </div>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Notas
              <textarea
                rows={2}
                value={modal.form.notes}
                onChange={(event) => patchVariation({ notes: event.target.value })}
                className={inputClass}
              />
            </label>
            <ImpactBox kind={modal.form.kind} amount={modal.form.amount} />
          </div>
        </Modal>
      )}

      {/* Modal — Rejeitar variação */}
      {modal?.kind === "reject" && (
        <Modal
          title="Rejeitar variação"
          onClose={closeModal}
          maxWidthClass="max-w-md"
          footer={
            <>
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 border border-border"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleReject}
                disabled={isSaving}
                className="px-4 py-2 bg-destructive/10 text-destructive disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? "A rejeitar…" : "Rejeitar variação"}
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <div className="text-sm text-secondary-text break-words">
              {modal.variation.description || "—"}
            </div>
            <label className="flex flex-col gap-1 text-sm text-secondary-text">
              Motivo da rejeição (opcional)
              <textarea
                rows={3}
                value={modal.form.rejectionReason}
                onChange={(event) =>
                  patchReject({ rejectionReason: event.target.value })
                }
                className={inputClass}
              />
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
}

// Preview ao vivo do impacto da variação no orçamento revisto.
function ImpactBox({ kind, amount }: { kind: WorkVariationKind; amount: string }) {
  const normalized = amount.trim().replace(/\s/g, "").replace(",", ".");
  const parsed = Number.parseFloat(normalized);
  const value = Number.isFinite(parsed) ? Math.abs(parsed) : 0;
  const isAddition = kind === "addition";
  return (
    <div
      className="bg-muted border border-border px-3 py-2 text-sm flex items-center justify-between gap-3 flex-wrap"
      style={{ borderRadius: "var(--radius)" }}
    >
      <span className="text-secondary-text">Impacto no orçamento revisto</span>
      <span
        className={`font-medium ${isAddition ? "text-green-700" : "text-destructive"}`}
      >
        {isAddition ? "+ " : "− "}
        {currencyFormatter.format(value)}
      </span>
    </div>
  );
}
